import React, { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import { CORE_NS } from 'recruiter-i18n/namespaces/core';
import { FeatureErrorBoundary } from 'components/FeatureErrorBoundary';
import { lazyWithReload } from 'utils/lazyWithReload';

import gs from './GlobalSearch.module.css';

// The dialog (search requests, results, breadcrumbs) loads on first open, so
// only this trigger is part of the app shell.
let modalLoaded = false;
const loadGlobalSearchModal = () =>
  import('./GlobalSearchModal').then((module) => {
    modalLoaded = true;
    return module;
  });
// React.lazy keeps a rejected import, so each attempt gets a fresh component;
// opening again after a failed load retries with a new one.
const createGlobalSearchModal = () => lazyWithReload(loadGlobalSearchModal);

const SIDE_NAV_SEARCH_FONT_SIZE = 13;

type GlobalSearchProps = {
  /** When the side nav is narrow/collapsed, hide the ⌘K hint so it doesn't overlap the icon. */
  isNavCollapsed?: boolean;
};

export default function GlobalSearch({
  isNavCollapsed = false,
}: GlobalSearchProps) {
  const { t } = useTranslation([SHELL_NS, CORE_NS]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // The dialog mounts on first open and stays mounted, keeping its query.
  const [hasOpened, setHasOpened] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const GlobalSearchModal = useMemo(createGlobalSearchModal, [loadAttempt]);

  const searchShortcutUsesCmd = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }
    return /Mac|iPhone|iPod|iPad/i.test(navigator.platform);
  }, []);

  const openModal = () => {
    if (hasOpened && !modalLoaded) {
      setLoadAttempt((attempt) => attempt + 1);
    }
    setIsModalOpen(true);
    setHasOpened(true);
  };

  // Platform modifier + K toggles global search (open or dismiss; avoid browser default)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'k' && e.key !== 'K') {
        return;
      }
      if (!(e.metaKey || e.ctrlKey) || e.altKey) {
        return;
      }
      e.preventDefault();
      if (isModalOpen) {
        setIsModalOpen(false);
      } else {
        openModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isModalOpen) {
      openModal();
    }
  };

  return (
    <div className={isNavCollapsed ? gs.collapsedWrap : undefined}>
      {/* Main Search Input */}
      {isNavCollapsed ? (
        <ActionIcon
          variant='default'
          radius='md'
          size={40}
          aria-label={t('search.open')}
          onClick={openModal}
          styles={{ root: { width: '100%' } }}
        >
          <IconSearch size={16} color='var(--mantine-color-dimmed)' />
        </ActionIcon>
      ) : (
        <div className={gs.searchTrigger}>
          {/* Read-only: typing happens in the dialog, which may still be loading. */}
          <TextInput
            readOnly
            placeholder={t('core:search.placeholder')}
            onFocus={openModal}
            onClick={!isModalOpen ? openModal : undefined}
            onKeyDown={handleTriggerKeyDown}
            leftSection={<IconSearch size={16} />}
            rightSection={
              <Text
                component='span'
                size='xs'
                pr='sm'
                className={gs.shortcutHint}
              >
                {/* i18n-ok: keyboard shortcut glyphs */}
                {searchShortcutUsesCmd ? '⌘ K' : '^ K'}
              </Text>
            }
            rightSectionPointerEvents='none'
            radius='lg'
            className={gs.searchInput}
            classNames={{ input: gs.searchTriggerInput }}
            styles={{
              input: {
                fontSize: SIDE_NAV_SEARCH_FONT_SIZE,
                lineHeight: 1.2,
              },
            }}
          />
        </div>
      )}

      {hasOpened && (
        <FeatureErrorBoundary
          feature='global_search'
          variant='silent'
          resetKeys={[loadAttempt]}
        >
          <React.Suspense fallback={null}>
            <GlobalSearchModal
              opened={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
          </React.Suspense>
        </FeatureErrorBoundary>
      )}
    </div>
  );
}
